import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MapPin, Search, Plus, ChevronRight, ChevronLeft, ArrowUpDown,
  Download, MoreHorizontal, Eye, Trash2, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LieuxPage = () => {
  const navigate = useNavigate();
  const [lieux, setLieux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchName, setSearchName] = useState('');
  const [perPage, setPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [openMenu, setOpenMenu] = useState(null);
  const [visibleCols, setVisibleCols] = useState(['name', 'programmes_count', 'ville', 'capacite']);
  const [showColPicker, setShowColPicker] = useState(false);

  const ALL_COLUMNS = [
    { id: 'name', label: 'Nom' },
    { id: 'programmes_count', label: 'Utilisation dans des programmes' },
    { id: 'ville', label: 'Ville' },
    { id: 'capacite', label: 'Capacité' },
    { id: 'code_postal', label: 'Code postal' },
    { id: 'contact_nom', label: 'Contact' },
    { id: 'contact_tel', label: 'Tél. contact' },
    { id: 'prix_journee', label: 'Prix/jour' },
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/donnees-lieux`, { credentials: 'include' });
      if (res.ok) setLieux(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce lieu ?")) return;
    await fetch(`${API}/donnees-lieux/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Lieu supprimé');
    setOpenMenu(null);
    fetchData();
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginatedData.map(l => l.lieu_id)));
  };

  const toggleCol = (id) => {
    setVisibleCols(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const filtered = lieux.filter(l => {
    const q = searchQuery.toLowerCase();
    const matchGlobal = !q || `${l.name} ${l.ville} ${l.contact_nom}`.toLowerCase().includes(q);
    const matchName = !searchName || (l.name || '').toLowerCase().includes(searchName.toLowerCase());
    return matchGlobal && matchName;
  }).sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginatedData = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <Layout>
      <div className="space-y-5" data-testid="lieux-page">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">Lieux de formation</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900" data-testid="page-title">Lieux de formation</h1>

        <div className="flex items-center gap-3 flex-wrap" data-testid="action-buttons">
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => navigate('/donnees/lieux/nouveau')} data-testid="add-lieu-btn">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un lieu de formation
          </Button>
          <Button variant="outline" onClick={() => toast.info("Export en cours de développement")} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" /> Exporter
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="lieux-table-card">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Rechercher" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} className="pl-10" data-testid="search-global" />
            </div>
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowColPicker(!showColPicker)} data-testid="col-picker-btn">
                Choisir les colonnes
              </Button>
              {showColPicker && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg py-2 z-30 w-64" data-testid="col-picker-dropdown">
                  {ALL_COLUMNS.map(col => (
                    <button key={col.id} onClick={() => toggleCol(col.id)} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-violet-50 transition-colors">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${visibleCols.includes(col.id) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
                        {visibleCols.includes(col.id) && <Check className="h-3 w-3 text-white" />}
                      </div>
                      {col.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="lieux-table">
              <thead>
                <tr className="bg-slate-50/80">
                  <th className="w-12 px-4 py-3">
                    <button onClick={toggleSelectAll} className={`w-5 h-5 rounded border flex items-center justify-center ${selectedIds.size === paginatedData.length && paginatedData.length > 0 ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid="select-all-checkbox">
                      {selectedIds.size === paginatedData.length && paginatedData.length > 0 && <Check className="h-3 w-3 text-white" />}
                    </button>
                  </th>
                  {visibleCols.map(colId => {
                    const col = ALL_COLUMNS.find(c => c.id === colId);
                    return (
                      <th key={colId} className="text-left px-4 py-3">
                        <button onClick={() => toggleSort(colId)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700" data-testid={`sort-${colId}`}>
                          {col?.label} <ArrowUpDown className="h-3 w-3" />
                        </button>
                      </th>
                    );
                  })}
                  <th className="w-12 px-4 py-3"></th>
                </tr>
                <tr className="bg-white border-b border-slate-100">
                  <td className="px-4 py-2"></td>
                  {visibleCols.map(colId => (
                    <td key={colId} className="px-4 py-2">
                      {colId === 'name' && (
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                          <input className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded bg-white" value={searchName} onChange={e => { setSearchName(e.target.value); setPage(1); }} data-testid="filter-name" />
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-2"></td>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i}><td colSpan={visibleCols.length + 2} className="px-4 py-3"><div className="h-8 bg-slate-100 rounded animate-pulse" /></td></tr>
                  ))
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="px-4 py-16 text-center">
                      <MapPin className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm text-slate-400">Aucun lieu de formation trouvé</p>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map(l => (
                    <tr key={l.lieu_id} className="hover:bg-violet-50/30 cursor-pointer transition-colors" onClick={() => navigate(`/donnees/lieux/${l.lieu_id}`)} data-testid={`lieu-row-${l.lieu_id}`}>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <button onClick={() => toggleSelect(l.lieu_id)} className={`w-5 h-5 rounded border flex items-center justify-center ${selectedIds.has(l.lieu_id) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
                          {selectedIds.has(l.lieu_id) && <Check className="h-3 w-3 text-white" />}
                        </button>
                      </td>
                      {visibleCols.map(colId => (
                        <td key={colId} className="px-4 py-3">
                          {colId === 'name' && <span className="text-sm text-violet-600 font-medium hover:underline">{l.name || ''}</span>}
                          {colId === 'programmes_count' && <span className="text-sm text-slate-600">{l.programmes_count ?? 0}</span>}
                          {colId === 'ville' && <span className="text-sm text-slate-600">{l.ville || ''}</span>}
                          {colId === 'capacite' && <span className="text-sm text-slate-600">{l.capacite || ''}</span>}
                          {colId === 'code_postal' && <span className="text-sm text-slate-600">{l.code_postal || ''}</span>}
                          {colId === 'contact_nom' && <span className="text-sm text-slate-600">{l.contact_nom || ''}</span>}
                          {colId === 'contact_tel' && <span className="text-sm text-slate-600">{l.contact_tel || ''}</span>}
                          {colId === 'prix_journee' && <span className="text-sm text-slate-600">{l.prix_journee ? `${l.prix_journee} €` : ''}</span>}
                        </td>
                      ))}
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="relative">
                          <button onClick={() => setOpenMenu(openMenu === l.lieu_id ? null : l.lieu_id)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid={`menu-${l.lieu_id}`}>
                            <MoreHorizontal className="h-4 w-4 text-slate-400" />
                          </button>
                          {openMenu === l.lieu_id && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-20 w-44">
                              <button onClick={() => navigate(`/donnees/lieux/${l.lieu_id}`)} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-violet-50 transition-colors">
                                <Eye className="h-4 w-4" /> Voir / Modifier
                              </button>
                              <button onClick={e => handleDelete(l.lieu_id, e)} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors">
                                <Trash2 className="h-4 w-4" /> Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between" data-testid="pagination">
              <div className="flex items-center gap-1">
                {[100, 200, 500].map(n => (
                  <button key={n} onClick={() => { setPerPage(n); setPage(1); }} className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${perPage === n ? 'bg-violet-100 text-violet-700' : 'text-slate-500 hover:bg-slate-100'}`}>{n}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>Page {page} sur {totalPages} ({filtered.length} éléments)</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 disabled:opacity-30" data-testid="prev-page"><ChevronLeft className="h-4 w-4" /></button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 disabled:opacity-30" data-testid="next-page"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LieuxPage;
